import { SortType } from '../components/ColumnHeader';
import { MaximumPreRegInvitesPerPage } from '../utils/eventConstants';

/* eslint-disable no-param-reassign */
export const save = (axiosInstance, eventValues) => {
    if (eventValues.id) {
        return axiosInstance.put(`v1/tms/events/${eventValues.id}`,
            eventValues);
    }

    return axiosInstance.post('v1/tms/events',
        eventValues);
};

export const createDualMatchEvent = (axiosInstance, eventValues) => {
    return axiosInstance.post('v1/tms/dualmatchevents', eventValues);
};

export const cloneDualMatchEvent = (axiosInstance, eventValues, cloneEventId) => {
    return axiosInstance.post(`v1/tms/dualmatchevents/${cloneEventId}/clone`, eventValues);
};

export const updateDualMatchEvent = (axiosInstance, eventValues) => {
    const { id, ...rest } = eventValues;
    return axiosInstance.post(`v1/tms/dualmatchevents/${eventValues.id}`, { ...rest });
};

export const getR2 = (axiosInstance, eventId) => {
    return axiosInstance.get(`v1/tms/events/r2/${eventId}`);
};

export const importR2 = (axiosInstance, eventId, values) => {
    return axiosInstance.post(`v1/tms/events/import?eventId=${eventId}`, values);
};

export const get = (axiosInstance, eventId, editView, optimized) => {
    const params = {
        optimized,
    };
    if (editView) {
        params.typesAsObjects = false;
        return axiosInstance.get(`v1/tms/events/${eventId}`, {
            params,
        });
    }
    return axiosInstance.get(`v1/tms/events/${eventId}`, {
        params,
    });
};

export const getDrawsView = (axiosInstance, eventId) => {
    // HUB-1058: temporarily points back at the legacy v1 endpoint because the
    // lean /draws-view projection ships ~34 MB (an internal back-reference
    // serializes inline when the response root isn't TMSEventViewModel).
    // Restore to `${eventId}/draws-view` once the backend fix lands.
    return axiosInstance.get(`v1/tms/events/${eventId}`);
};

export const sendInvitationToGroup = (axiosInstance, eventId, clubId) => {
    const url = `v1/tms/events/${eventId}/sendinvitationtogroup/${clubId}`;
    return axiosInstance.post(url);
};

export const getDraws = (axiosInstance, eventId) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/draws`);
};

export const getEventLocations = (axiosInstance, eventId) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/locations`);
};

export const getEventFacilities = (axiosInstance, eventId) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/facilities`);
};

export const addCourtToEventExclusions = (axiosInstance, eventId, payload) => {
    return axiosInstance.post(`v1/tms/events/${eventId}/locations`, payload);
};

export const removeCourtFromEventExclusions = (axiosInstance, eventId, payload) => {
    return axiosInstance.delete(`v1/tms/events/${eventId}/locations`, { data: payload });
};

export const getEventOfficials = (axiosInstance, eventId) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/officials`);
};

export const saveEventOfficial = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`v1/tms/events/${eventId}/officials`, data);
};

export const updateEventOfficial = (axiosInstance, eventId, officialId, data) => {
    return axiosInstance.put(`v1/tms/events/${eventId}/officials/${officialId}`, data);
};

export const inactivateEventOfficial = (axiosInstance, eventId, officialId) => {
    return axiosInstance.delete(`v1/tms/events/${eventId}/officials/${officialId}`);
};

export const getMatchOfficials = (axiosInstance, eventId, matchId) => {
    return axiosInstance.get(`/v1/tms/events/${eventId}/officials/match/${matchId}`);
};

export const addMatchOfficial = (axiosInstance, eventId, drawId, matchId, payload) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/draws/${drawId}/matches/${matchId}/officials`, payload);
};

export const deleteMatchOfficial = (axiosInstance, eventId, drawId, matchId, officialId) => {
    return axiosInstance.delete(`/v1/tms/events/${eventId}/draws/${drawId}/matches/${matchId}/officials/${officialId}`);
};

export const postMemberRegistration = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/register`, data);
};

export const registerPlayerByOrganizer = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/add-player`, data);
};

export const registerPlayerByOrganizerV2 = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/add-player`, data);
};

export const addEligiblePlayersV2 = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/eligibleplayers`, data);
};

export const getEligiblePlayersV2 = (axiosInstance, eventId, page, searchTerm) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/eligibleplayers?page=${page}&size=100&search=${searchTerm}`);
};

export const removeEligiblePlayerV2 = (axiosInstance, eventId, playerId) => {
    const url = `/v2/tms/events/${eventId}/eligibleplayers/${playerId}`;
    return axiosInstance.delete(url);
};

export const sendPreRegistrationInvitesV2 = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`v2/tms/events/${eventId}/preregistrationinvites`, data);
};

export const bulkReSendPreRegistrationInvites = (axiosInstance, eventId, data) => {
    return axiosInstance.put(`v2/tms/events/${eventId}/preregistrationinvites/resend/bulk`, data);
};

export const bulkDeletePreRegistrationInvites = (axiosInstance, eventId, data) => {
    return axiosInstance.delete(`v2/tms/events/${eventId}/preregistrationinvites/bulk`, {
        data,
    });
};

export const bulkExtendPreRegistrationInvites = (axiosInstance, eventId, data) => {
    return axiosInstance.put(`v2/tms/events/${eventId}/preregistrationinvites/extend/bulk`, data);
};

export const getMemberIdsFromPreRegInvites = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`v2/tms/events/${eventId}/preregistrationinvites/memberids`, data);
};

export const getPreRegistrationInviteListV2 = (axiosInstance, eventId, page, filters = {}, sortData = {}) => {
    let orderByRules = null;
    if (sortData?.column && sortData?.type) {
        orderByRules = sortData.type === SortType.ASC ? 'Ascending' : 'Descending';
    }
    const postData = {
        page,
        size: MaximumPreRegInvitesPerPage,
        orderBy: sortData?.column,
        orderByRules,
        ...filters,
    };
    return axiosInstance.post(`/v2/tms/events/${eventId}/preregistrationinvites/search`, postData);
};

export const resendPreRegistrationInviteV2 = (axiosInstance, eventId, invteId) => {
    return axiosInstance.put(`v2/tms/events/${eventId}/preregistrationinvites/${invteId}/resend`);
};

export const removePreRegistrationInviteV2 = (axiosInstance, eventId, inviteId) => {
    const url = `/v2/tms/events/${eventId}/preregistrationinvites/${inviteId}`;
    return axiosInstance.delete(url);
};

export const getPreRegInviteFilters = (axiosInstance, eventId) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/preregistrationinvites/filters`);
};

export const getPreRegStats = (axiosInstance, eventId) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/preregistrationinvites/stats`);
};

export const declinePreRegInvitationV2 = (axiosInstance, eventId, inviteId) => {
    return axiosInstance.put(`v2/tms/events/${eventId}/preregistrationinvites/${inviteId}/decline`);
};

export const getPreRegInviteStatus = (axiosInstance, ignoreNextPollUtc) => {
    return axiosInstance.get('v2/tms/events/preregistrationinvites/playerprompt', {
        params: {
            ignoreNextPollUtc,
        },
    });
};

export const validatePassword = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/password`, data);
};

export const validateUSAPId = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`/v3/tms/events/${eventId}/validateusapid`, data);
};

export const saveAnswers = (axiosInstance, eventId, data) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/answers`, data);
};

export const cloneEvent = (axiosInstance, eventId) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/clone`);
};

export const publishEvent = (axiosInstance, eventId) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/publish`);
};

export const unPublishEvent = (axiosInstance, eventId) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/unpublish`);
};

export const getRecurringEventsStatus = (axiosInstance, eventId) => {
    return axiosInstance.get(`/v1/tms/events/${eventId}/recurrent-events/status`);
};

export const getUpcomingRecurringEvents = (axiosInstance, eventId) => {
    return axiosInstance.get(`/v1/tms/events/${eventId}/recurrent-events`);
};

export const deleteEvent = (axiosInstance, eventId) => {
    return axiosInstance.delete(`/v1/tms/events/${eventId}`);
};

export const movePlayersToRound = (axiosInstance, eventId, divisionId, data) => {
    return axiosInstance.post(`v2/tms/events/${eventId}/divisions/${divisionId}/acceptance-list/move-member-to-round`, data);
};

export const movePlayersToMain = (axiosInstance, eventId, divisionId, data) => {
    return axiosInstance.put(`v2/tms/events/${eventId}/divisions/${divisionId}/waitlist/move-to-main`, data);
};

export const declineInvitation = (axiosInstance, eventId) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/invite/decline`);
};

export const getRegistrantExport = (axiosInstance, eventId) => {
    return axiosInstance.get(`/v1/tms/events/${eventId}/export`);
};

export const getSearchCriteria = (axiosInstance) => {
    const url = `v1/event/search/available-params`;
    return axiosInstance.get(url);
};

export const getGiud = (axiosInstance) => {
    const url = `v1/tms/guid`;
    return axiosInstance.get(url);
};

export const initDraws = (axiosInstance, eventId) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/draws/initialize`);
};

export const deleteDraw = (axiosInstance, eventId, drawId) => {
    return axiosInstance.delete(`/v1/tms/events/${eventId}/draws/${drawId}`);
};

export const resetEvent = (axiosInstance, eventId) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/reset`);
};

export const autofillDraw = (axiosInstance, eventId, drawId, event) => {
    event.draws.forEach(d => {
        if (d.rounds) {
            if (d.childDraws) {
                d.childDraws.forEach(c => {
                    c.rounds.forEach(r => {
                        r.matches.forEach(m => {
                            m.result = null;
                        });
                    });
                });
            }
            d.rounds.forEach(r => {
                r.matches.forEach(m => {
                    m.result = null;
                });
            });
        }
    });
    return axiosInstance.post(`/v1/tms/events/${eventId}/draws/${drawId}/populate`, event);
};

export const autofillDrawRound = (axiosInstance, eventId, drawId, roundId, selected, selectedTeams, event) => {
    event.draws.forEach(d => {
        if (d.rounds) {
            if (d.childDraws) {
                d.childDraws.forEach(c => {
                    c.rounds.forEach(r => {
                        r.matches.forEach(m => {
                            m.result = null;
                        });
                    });
                });
            }
            d.rounds.forEach(r => {
                r.matches.forEach(m => {
                    m.result = null;
                });
            });
        }
    });

    const data = {
        playerIds: selected,
        teamIds: selectedTeams,
        event,
    };

    return axiosInstance.post(`/v1/tms/events/${eventId}/draws/${drawId}/round/${roundId}/populate`, data);
};

export const publishDraws = (axiosInstance, eventId, event) => {
    event.draws.forEach(d => {
        if (d.rounds) {
            if (d.childDraws) {
                d.childDraws.forEach(c => {
                    c.rounds.forEach(r => {
                        r.matches.forEach(m => {
                            m.result = null;
                        });
                    });
                });
            }
            d.rounds.forEach(r => {
                r.matches.forEach(m => {
                    m.result = null;
                });
            });
        }
    });
    return axiosInstance.post(`/v1/tms/events/${eventId}/draws/publish`, event);
};

export const searchEvents = (axiosInstance, top, skip, filters) => {
    const url = `v1/event/search`;
    const params = {
        top,
        skip,
        month: filters.month,
        year: filters.year,
        country: filters.country,
        state: filters.state,
    };
    return axiosInstance.get(url, {
        params,
    });
};

export const saveDraws = (axiosInstance, event) => {
    event.draws.forEach(d => {
        if (d.rounds) {
            if (d.childDraws) {
                d.childDraws.forEach(c => {
                    c.rounds.forEach(r => {
                        r.matches.forEach(m => {
                            m.result = null;
                        });
                    });
                });
            }
            d.rounds.forEach(r => {
                r.matches.forEach(m => {
                    m.result = null;
                });
            });
        }
    });
    return axiosInstance.post(`v1/tms/events/${event.id}/draws/update`, event);
};

export const createAutomatedDraw = (axiosInstance, eventId, payload) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/automated-autofills`, payload);
};

export const saveMatchDates = (axiosInstance, data) => {
    const { eventId, drawId, matchDates } = data;
    return axiosInstance.post(`v1/tms/events/${eventId}/draws/${drawId}/matches/dates`, matchDates);
};

export const resizeBackDraw = (axiosInstance, data) => {
    const { eventId, drawId } = data;
    return axiosInstance.post(`v1/tms/events/${eventId}/draws/${drawId}/backdraw/resize`);
};

export const eventMeta = (axiosInstance, eventId) => {
    const url = `v1/tms/events/${eventId}/meta`;

    return axiosInstance.get(url);
};

export const getRegPayments = (axiosInstance, data) => {
    const { eventId, memberId } = data;
    const url = `/v1/tms/events/${eventId}/registration-payments/${memberId}`;

    return axiosInstance.get(url);
};

export const getDivisionAcceptanceList = (axiosInstance, data) => {
    const { eventId, divisionId } = data;
    return axiosInstance.get(`v1/tms/events/${eventId}/divisions/${divisionId}/acceptancelist`);
};

export const getDivisionAcceptanceListV2 = (axiosInstance, data) => {
    const { eventId, divisionId } = data;
    return axiosInstance.get(`v2/tms/events/${eventId}/divisions/${divisionId}/acceptance-list`);
};

export const getRegistrations = (axiosInstance, data) => {
    const { eventId } = data;
    return axiosInstance.get(`v2/tms/events/${eventId}/divisions/registrations`);
};

export const selfWithdraw = (axiosInstance, { eventId, userId, divisionIds }) => {
    const url = `v1/tms/events/${eventId}/self-withdraw?id=${userId}`;
    return axiosInstance.post(url, {
        divisionIds,
    });
};

export const getEventTemplate = (axiosInstance, templateId, clubId) => {
    // eslint-disable-next-line prefer-template
    return axiosInstance.get(`v1/tms/events/templates/${templateId}/preview${clubId ? '?clubId=' + clubId : ''}`);
};

export const getDrawOfficials = (axiosInstance, eventId, drawId) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/draws/${drawId}/officials`);
};

export const getEventDirectors = (axiosInstance, eventId) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/directors`);
};

export const addEventVolunteer = (axiosInstance, eventId, payload) => {
    return axiosInstance.post(`v1/tms/events/${eventId}/directors/add-volunteer`, payload);
};

export const addEventAdmin = (axiosInstance, eventId, payload) => {
    return axiosInstance.post(`v1/tms/events/${eventId}/directors/add-admin`, payload);
};

export const deleteEventDirector = (axiosInstance, eventId, memberId) => {
    return axiosInstance.delete(`v1/tms/events/${eventId}/directors/${memberId}`);
};

export const getCheckInPlayers = (axiosInstance, eventId, query, date, sortType) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/check-in-desk/members?query=${query}&drawSpecificDate=${date}&nameOrdering=${sortType}`);
};

export const getPlayerInfoForCheckIn = (axiosInstance, eventId, memberId) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/check-in-desk/member/${memberId}`);
};

export const submitPlayerCheckIn = (axiosInstance, eventId, memberId, payload) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/check-in-desk/member/${memberId}/draws`, payload);
};

export const getMatchSheet = (axiosInstance, eventId) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/match-sheet`);
};

export const updateMatchSheet = (axiosInstance, eventId, matchId, payload) => {
    return axiosInstance.put(`v1/tms/events/${eventId}/matches/${matchId}/match-sheet`, payload);
};

export const updateMatchSheetRound = (axiosInstance, eventId, roundId, payload) => {
    return axiosInstance.put(`v1/tms/events/${eventId}/rounds/${roundId}/match-sheet`, payload);
};

export const deleteMatchSheet = (axiosInstance, eventId, matchId) => {
    return axiosInstance.delete(`v1/tms/events/${eventId}/matches/${matchId}/match-sheet`);
};

export const updateScheduleType = (axiosInstance, eventId, payload) => {
    return axiosInstance.put(`v1/tms/events/${eventId}/draw-schedule`, payload);
};

export const addTeamStanding = (axiosInstance, eventId, teamStanding) => {
    const url = `/v1/tms/events/${eventId}/league-standings`;
    return axiosInstance.post(url, teamStanding);
};

export const getTeamStandings = (axiosInstance, eventId) => {
    const url = `/v1/tms/events/${eventId}/league-standings`;
    return axiosInstance.get(url);
};

export const editTeamStanding = (axiosInstance, eventId, teamStanding) => {
    const url = `/v1/tms/events/${eventId}/league-standings/${teamStanding.id}`;
    return axiosInstance.put(url, {
        ...teamStanding,
    });
};

export const deleteTeamStanding = (axiosInstance, eventId, standingId) => {
    const url = `/v1/tms/events/${eventId}/league-standings/${standingId}`;
    return axiosInstance.delete(url);
};

export const includeInHighSchoolStandings = (axiosInstance, eventId) => {
    const url = `/v1/tms/events/${eventId}/includeconference`;
    return axiosInstance.post(url);
};

export const omitFromHighSchoolStandings = (axiosInstance, eventId) => {
    const url = `/v1/tms/events/${eventId}/omitconference`;
    return axiosInstance.post(url);
};

export const fetchDrawPlayersContactInfo = (axiosInstance, eventId, drawId) => {
    const url = `/v1/tms/events/${eventId}/draws/${drawId}/contacts`;
    return axiosInstance.get(url);
};

export const getEventSafeSport = (axiosInstance, eventId) => {
    const url = `v1/tms/events/${eventId}/safesport`;
    return axiosInstance.get(url);
};

export const getAdultTeamTennisStandings = (axiosInstance, clubId) => {
    const url = `v1/teams/${clubId}/standings`;
    return axiosInstance.get(url);
};

export const includeInAttStandings = (axiosInstance, eventId) => {
    const url = `/v1/tms/events/${eventId}/includeconference`;
    return axiosInstance.post(url);
};

export const omitFromAttStandings = (axiosInstance, eventId) => {
    const url = `/v1/tms/events/${eventId}/omitconference`;
    return axiosInstance.post(url);
};

export const getInvitations = (axiosInstance) => {
    const url = `/v1/tms/invitations`;
    return axiosInstance.get(url);
};

export const acceptInvite = (axiosInstance, eventId, divisionId) => {
    const division = { divisions: [{ divisionId }] };
    const url = `/v2/tms/events/${eventId}/register`;
    return axiosInstance.post(url, division);
};

export const declineInvite = (axiosInstance, eventId, divisionId) => {
    const division = { divisionIds: [divisionId] };
    const url = `/v1/tms/events/${eventId}/self-withdraw`;
    return axiosInstance.post(url, division);
};

export const getRestrictedDivisions = (axiosInstance, eventId) => {
    const url = `/v2/tms/events/${eventId}/division-registration-restrictions`;
    return axiosInstance.get(url);
};
export const getEventRegions = (axiosInstance, eventId) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/regions`);
};

export const requestPaidHitForPlayer = (axiosInstance, requestedPlayerId, message) => {
    return axiosInstance.post('/v1/tms/paid-hits/request', requestedPlayerId, message);
};

export const setPlayerWildcard = (axiosInstance, { eventId, divisionId, memberId, wildcardStateId }) => {
    return axiosInstance.put(`/v2/tms/events/${eventId}/divisions/${divisionId}/acceptance-list/set-wildcard`, { memberId, wildcardStateId });
};

export const getOrganizerRatings = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v1/tms/events/${eventId}/getOrganizerRatings`);
};

export const setOrganizerRating = (axiosInstance, { eventId, memberId, singlesRating, doublesRating }) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/setOrganizerRatings`, { memberId, SinglesOrganizerRating: singlesRating, DoublesOrganizerRating: doublesRating });
};

export const setDrawTag = (axiosInstance, { eventId, drawId, memberId, teamId, tagId }) => {
    if (memberId) {
        return axiosInstance.post(`v2/tms/events/${eventId}/draws/${drawId}/members/tag`, { memberId, tagId });
    }
    return axiosInstance.post(`v2/tms/events/${eventId}/draws/${drawId}/teams/tag`, { teamId, tagId });
};

// draw editor v2 endpoints
export const getEventV2 = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}`);
};
export const getDivisionCounts = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/division-counts`);
};
export const getFeatureFlagParameterForEvent = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/feature-flag-parameters`);
};
export const toggleEventSMSEnabled = (axiosInstance, { eventId, smsEnabled }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/event-desk/toggle-sms/${smsEnabled ? 'true' : 'false'}`);
};
export const getDrawEditEvent = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/event-desk`);
};
export const getDrawEditDraws = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/draws`);
};
export const getDrawEditPlayers = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/players`);
};
export const getAllPlayers = (axiosInstance, { eventId }) => { // v3 get /players endpoint
    return axiosInstance.get(`/v2/tms/events/${eventId}/all-players`);
};
export const getWithdrawnPlayers = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/players/withdrawn`);
};
export const getDrawEditTeams = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/teams`);
};
export const getDrawEditDraw = (axiosInstance, { eventId, drawId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/draws/${drawId}`);
};
export const getEventProfileDraw = (axiosInstance, { eventId, drawId }) => {
    return axiosInstance.get(`/v3/tms/events/${eventId}/draws/${drawId}`);
};
export const getMatch = (axiosInstance, { eventId, drawId, matchId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/draws/${drawId}/matches/${matchId}`);
};
export const getEventProfileMatch = (axiosInstance, { eventId, drawId, matchId }) => {
    return axiosInstance.get(`/v3/tms/events/${eventId}/draws/${drawId}/matches/${matchId}`);
};
export const getOrderOfPlayMatches = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v1/tms/events/${eventId}/order-of-play/matches`);
};
export const getOrderOfPlaySchedule = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/order-of-play/schedule`);
};
export const getMatchSheetMatches = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v1/tms/events/${eventId}/match-sheet/matches`);
};
export const getMatchSheetV2 = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/match-sheet`);
};
export const getCurrentMemberRegistration = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/current-member/registrations`);
};
export const getMatchSheetData = (axiosInstance, { eventId, drawId }) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/draws/${drawId}/matchsheetdata`);
};
export const getDrawAllStats = (axiosInstance, { eventId, drawId }) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/draws/${drawId}/all-stats`);
};
export const createDraw = (axiosInstance, { eventId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws`, payload);
};
export const createProDraw = (axiosInstance, { eventId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/prodraw`, payload);
};

export const updateProDraw = (axiosInstance, { eventId, payload }) => {
    return axiosInstance.put(`/v2/tms/events/${eventId}/draws/prodraw`, payload);
};

export const movePlayers = (axiosInstance, { eventId, fromDrawId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/${fromDrawId}/members-and-teams/move`, payload);
};
export const updateDrawMembers = (axiosInstance, { eventId, drawId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/${drawId}/members-and-teams/update`, payload);
};
export const updateDrawWaitlist = (axiosInstance, { eventId, drawId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/${drawId}/members-and-teams/waitlist`, payload);
};
export const updateMatches = (axiosInstance, { eventId, drawId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/${drawId}/matches`, payload);
};
export const autofillDrawV2 = (axiosInstance, eventId, drawId, seedSettings) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/${drawId}/autofill`, seedSettings);
};
export const autofillDrawRoundV2 = (axiosInstance, eventId, drawId, roundId, selected, selectedTeams) => {
    const data = {
        memberIds: selected,
        teamIds: selectedTeams,
    };
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/${drawId}/rounds/${roundId}/autofill`, data);
};
export const updateHideSeedNumbers = (axiosInstance, eventId, drawId, payload) => {
    return axiosInstance.put(`v2/tms/events/${eventId}/draws/${drawId}/updatehideseednumbers`, payload);
};
export const publishDrawsV2 = (axiosInstance, { eventId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/publish`, payload);
};
export const updateDraw = (axiosInstance, { eventId, drawId, payload }) => {
    return axiosInstance.put(`/v2/tms/events/${eventId}/draws/${drawId}`, payload);
};
export const sendSchedulingMessage = (axiosInstance, eventId) => {
    return axiosInstance.post(`/v1/tms/events/${eventId}/draws/notify`, {});
};
export const getDrawStats = (axiosInstance, { eventId, drawId }) => {
    return axiosInstance.get(`v1/tms/events/${eventId}/draws/${drawId}/stats`);
};
export const getEventInvites = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/invites`);
};
export const addPartner = (axiosInstance, { eventId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/registrations/add-partners`, payload);
};

export const lookupRegistrations = (axiosInstance, eventId, memberIds, signal) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/registrations/lookup`, { memberIds }, { signal });
};

export const splitDivisionTeam = (axiosInstance, eventId, registrationGroupId) => {
    return axiosInstance.put(`v2/tms/events/${eventId}/registrations/split-group/${registrationGroupId}`);
};

export const removePlayers = (axiosInstance, eventId, memberId, registrationGroupId) => {
    return axiosInstance.put(`v2/tms/events/${eventId}/registrations/remove-players/${memberId}/${registrationGroupId}`);
};

export const validateDivisionPartner = (axiosInstance, eventId, divisionId, playerId) => {
    return axiosInstance.get(`v2/tms/events/${eventId}/divisions/${divisionId}/registrations/do-partner-check/${playerId}`);
};

export const createWeatherNotification = (axiosInstance, eventId, notificationText) => {
    const url = `v2/tms/events/${eventId}/weather`;
    return axiosInstance.post(url, {
        notificationText,
    });
};

export const updateWeatherNotification = (axiosInstance, eventId, notificationId, notificationText) => {
    const url = `v2/tms/events/${eventId}/weather/${notificationId}`;
    return axiosInstance.put(url, {
        notificationText,
    });
};

export const deleteWeatherNotification = (axiosInstance, eventId, notificationId) => {
    return axiosInstance.delete(`v2/tms/events/${eventId}/weather/${notificationId}`);
};

export const getAllWeatherNotifications = (axiosInstance, eventId) => {
    return axiosInstance.get(`v2/tms/events/${eventId}/weather`);
};

export const getDrawDataForEdit = (axiosInstance, eventId, drawId) => {
    return axiosInstance.get(`v2/tms/events/${eventId}/draws/${drawId}/edit`);
};

export const updatePrintMatchSheet = (axiosInstance, { eventId, drawId, payload }) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/${drawId}/matches/print-match-sheet`, payload);
};

export const confirmDivisionDrawAssociation = (axiosInstance, { eventId }) => {
    return axiosInstance.put(`/v1/tms/events/${eventId}/confirmDivisionDrawAssociation`, {
        confirmDivisionDrawAssociation: true,
    });
};

export const getDivisionDrawAssociation = (axiosInstance, { eventId }) => {
    return axiosInstance.get(`v2/tms/events/${eventId}/drawdivision`);
};

export const getPotentialPartners = (axiosInstance, eventId, divisionId, memberId) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/divisions/${divisionId}/potential-partners/${memberId}`);
};

export const getMedalPrintData = (axiosInstance, { eventId, drawId }) => {
    return axiosInstance.get(`v2/tms/events/${eventId}/draws/${drawId}/medalprintdata`);
};

export const getNeedsPartnerPlayers = (axiosInstance, { eventId, divisionId }) => {
    return axiosInstance.get(`v2/tms/events/${eventId}/needspartnerplayers/${divisionId}`);
};

export const deleteDrawV2 = (axiosInstance, eventId, drawId) => {
    return axiosInstance.delete(`/v2/tms/events/${eventId}/draws/${drawId}`);
};

export const getRegisteredDoublesDivisions = (axiosInstance, eventId) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/divisions/registrations/doubles`);
};
export const updateRegistrationPartners = (axiosInstance, eventId, payload) => {
    return axiosInstance.put(`/v2/tms/events/${eventId}/registrations/partners/update`, payload);
};

export const getDrawsPublishInfo = (axiosInstance, eventId) => {
    return axiosInstance.get(`/v2/tms/events/${eventId}/draws/for-publish`);
};

export const scheduleAutoPublishDraws = (axiosInstance, eventId, payload) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/publish/schedule`, payload);
};

export const deleteAutoPublishScheduled = (axiosInstance, eventId, payload) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/draws/publish/schedule/delete`, payload);
};

export const sendGroupSMS = (axiosInstance, eventId, payload) => {
    return axiosInstance.post(`/v2/tms/events/${eventId}/send-group-sms`, payload);
};
